// Página AdminShipping - Configuración de envío
// Permite configurar las reglas de envío gratis y costos de envío

import { useState, useEffect, useMemo } from 'react'
import { MapContainer, Marker, TileLayer, useMap, useMapEvents } from 'react-leaflet'
import L from 'leaflet'
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png'
import markerIcon from 'leaflet/dist/images/marker-icon.png'
import markerShadow from 'leaflet/dist/images/marker-shadow.png'
import { useShipping } from '../../hooks/useShipping'
import { saveShippingSettings } from '../../services/shipping.service'
import { getAllWarehousesIncludingInactive } from '../../services/warehouses.service'
import { buildAddressQuery, geocodeAddress, normalizeCoordinates } from '../../utils/helpers'
import Button from '../../components/Button'
import 'leaflet/dist/leaflet.css'
import './AdminShipping.css'

/**
 * Página AdminShipping
 * Configuración de reglas de envío
 */
const formatArs = (value) => {
  const n = Number(value)
  if (!Number.isFinite(n)) return ''
  return new Intl.NumberFormat('es-AR', {
    style: 'currency',
    currency: 'ARS',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })
    .format(n)
    .replace(/\u00a0/g, ' ')
}

const parseArsNumber = (value) => {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null
  }
  const raw = String(value || '').trim()
  if (!raw) return null
  const cleaned = raw.replace(/[^\d,.-]/g, '').replace(/\./g, '').replace(/,/g, '.')
  const n = Number(cleaned)
  return Number.isFinite(n) ? n : null
}

const sanitizeArsInput = (value) => String(value || '').replace(/[^\d,.-]/g, '')

const MapRecenter = ({ center }) => {
  const map = useMap()
  useEffect(() => {
    if (center) {
      map.setView(center)
    }
  }, [center, map])
  return null
}

const MapClickHandler = ({ onChange, disabled }) => {
  useMapEvents({
    click: (event) => {
      if (disabled) return
      onChange?.({ lat: event.latlng.lat, lng: event.latlng.lng })
    }
  })
  return null
}

const DraggableMarker = ({ position, onChange, disabled, icon }) => {
  const eventHandlers = useMemo(() => ({
    dragend: (event) => {
      const next = event.target.getLatLng()
      onChange?.({ lat: next.lat, lng: next.lng })
    }
  }), [onChange])
  return (
    <Marker
      draggable={!disabled}
      eventHandlers={eventHandlers}
      position={position}
      icon={icon}
    />
  )
}

function AdminShipping() {
  const { settings, loading, error, refetch } = useShipping()
  const [formData, setFormData] = useState({
    freeShippingMinAmount: 50000,
    freeShippingMaxDistance: null,
    defaultShippingCost: 15000,
    homeDeliveryEnabled: true,
    pickupEnabled: false,
    pickupWarehouseIds: [],
    originAddress: { street: '', apartment: '', city: '', postalCode: '', province: '', country: 'AR' },
    originCoordinates: { lat: '', lng: '' },
    distanceTiers: [{ maxDistance: '', cost: '' }],
    negotiationDistance: '',
    negotiationEnabled: false
  })
  const [saving, setSaving] = useState(false)
  const [saveMessage, setSaveMessage] = useState(null)
  const [warehouses, setWarehouses] = useState([])
  const [warehousesLoading, setWarehousesLoading] = useState(false)
  const [warehousesError, setWarehousesError] = useState(null)
  const [originGeoStatus, setOriginGeoStatus] = useState(null)
  const [originMapPosition, setOriginMapPosition] = useState(null)
  const markerIconConfig = useMemo(() => new L.Icon({
    iconUrl: markerIcon,
    iconRetinaUrl: markerIcon2x,
    shadowUrl: markerShadow,
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
    shadowSize: [41, 41]
  }), [])
  const originLat = formData.originCoordinates?.lat
  const originLng = formData.originCoordinates?.lng

  // Cargar configuraciones cuando se obtengan
  useEffect(() => {
    if (settings) {
      const fulfillment = settings.fulfillment || {}
      const distancePricing = settings.distancePricing || {}
      const originAddress = distancePricing.originAddress || {}
      const originCoordinates = distancePricing.originCoordinates || {}
      const tiers = Array.isArray(distancePricing.tiers) ? distancePricing.tiers : []
      setFormData({
        freeShippingMinAmount: settings.freeShippingMinAmount || 50000,
        freeShippingMaxDistance: settings.freeShippingMaxDistance || null,
        defaultShippingCost: settings.defaultShippingCost || 15000,
        homeDeliveryEnabled: fulfillment.homeDeliveryEnabled !== false,
        pickupEnabled: fulfillment.pickupEnabled === true,
        pickupWarehouseIds: Array.isArray(fulfillment.pickupWarehouseIds) ? fulfillment.pickupWarehouseIds : [],
        originAddress: {
          street: originAddress.street || '',
          apartment: originAddress.apartment || '',
          city: originAddress.city || '',
          postalCode: originAddress.postalCode || '',
          province: originAddress.province || '',
          country: originAddress.country || 'AR'
        },
        originCoordinates: {
          lat: originCoordinates?.lat ?? '',
          lng: originCoordinates?.lng ?? ''
        },
        distanceTiers: tiers.length
          ? tiers.map((t) => ({
            maxDistance: t.maxDistance ?? '',
            cost: t.cost ?? ''
          }))
          : [{ maxDistance: '', cost: '' }],
        negotiationDistance: distancePricing.negotiationDistance ?? '',
        negotiationEnabled: distancePricing.negotiationEnabled === true
      })
    }
  }, [settings])

  useEffect(() => {
    let cancelled = false
    const run = async () => {
      setWarehousesLoading(true)
      setWarehousesError(null)
      try {
        const rows = await getAllWarehousesIncludingInactive()
        if (!cancelled) setWarehouses(Array.isArray(rows) ? rows : [])
      } catch (e) {
        if (!cancelled) setWarehousesError(e?.message || 'Error desconocido')
      } finally {
        if (!cancelled) setWarehousesLoading(false)
      }
    }
    run()
    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    const normalized = normalizeCoordinates({ lat: originLat, lng: originLng })
    if (!normalized) return
    setOriginMapPosition(prev => {
      if (!prev) return normalized
      const sameLat = Math.abs(prev.lat - normalized.lat) < 0.000001
      const sameLng = Math.abs(prev.lng - normalized.lng) < 0.000001
      return sameLat && sameLng ? prev : normalized
    })
  }, [originLat, originLng])

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target
    if (name.startsWith('originAddress.')) {
      const key = name.split('.')[1]
      setFormData(prev => ({
        ...prev,
        originAddress: { ...prev.originAddress, [key]: value }
      }))
      return
    }
    if (name.startsWith('originCoordinates.')) {
      const key = name.split('.')[1]
      setFormData(prev => ({
        ...prev,
        originCoordinates: { ...prev.originCoordinates, [key]: value }
      }))
      return
    }
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : (value === '' ? null : value)
    }))
  }

  const handleGeocodeOrigin = async () => {
    if (!formData.homeDeliveryEnabled) return
    const query = buildAddressQuery(formData.originAddress)
    if (!query) {
      setOriginGeoStatus({ type: 'error', text: 'Completá la dirección de origen para geolocalizar.' })
      return
    }
    setOriginGeoStatus({ type: 'loading', text: 'Geolocalizando dirección de origen...' })
    const geocoded = await geocodeAddress(query)
    if (!geocoded || !Number.isFinite(geocoded.lat) || !Number.isFinite(geocoded.lng)) {
      setOriginGeoStatus({ type: 'error', text: 'No se pudo geolocalizar la dirección. Revisá los datos.' })
      return
    }
    const next = { lat: geocoded.lat, lng: geocoded.lng }
    setFormData(prev => ({
      ...prev,
      originCoordinates: { lat: next.lat.toFixed(6), lng: next.lng.toFixed(6) }
    }))
    setOriginMapPosition(next)
    setOriginGeoStatus({ type: 'success', text: geocoded.displayName || 'Origen geolocalizado.' })
  }

  const handleMapPositionChange = (coords) => {
    if (!coords || !Number.isFinite(coords.lat) || !Number.isFinite(coords.lng)) return
    setFormData(prev => ({
      ...prev,
      originCoordinates: { lat: coords.lat.toFixed(6), lng: coords.lng.toFixed(6) }
    }))
    setOriginMapPosition(coords)
  }

  const updateTier = (index, field, value) => {
    setFormData(prev => {
      const next = Array.isArray(prev.distanceTiers) ? [...prev.distanceTiers] : []
      next[index] = { ...next[index], [field]: value }
      return { ...prev, distanceTiers: next }
    })
  }

  const handleTierCostChange = (index, value) => {
    updateTier(index, 'cost', sanitizeArsInput(value))
  }

  const handleTierCostBlur = (index) => {
    const parsed = parseArsNumber(formData.distanceTiers?.[index]?.cost)
    if (parsed === null) return
    updateTier(index, 'cost', formatArs(parsed))
  }

  const handleTierCostFocus = (index) => {
    const parsed = parseArsNumber(formData.distanceTiers?.[index]?.cost)
    if (parsed === null) return
    updateTier(index, 'cost', parsed.toFixed(2).replace('.', ','))
  }

  const addTier = () => {
    setFormData(prev => ({
      ...prev,
      distanceTiers: [...(prev.distanceTiers || []), { maxDistance: '', cost: '' }]
    }))
  }

  const removeTier = (index) => {
    setFormData(prev => {
      const next = Array.isArray(prev.distanceTiers) ? prev.distanceTiers.filter((_, i) => i !== index) : []
      return { ...prev, distanceTiers: next.length ? next : [{ maxDistance: '', cost: '' }] }
    })
  }

  const togglePickupWarehouse = (warehouseId) => {
    setFormData(prev => {
      const current = Array.isArray(prev.pickupWarehouseIds) ? prev.pickupWarehouseIds : []
      const has = current.includes(warehouseId)
      const next = has ? current.filter((x) => x !== warehouseId) : [...current, warehouseId]
      return { ...prev, pickupWarehouseIds: next }
    })
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setSaving(true)
    setSaveMessage(null)

    try {
      if (!formData.homeDeliveryEnabled && !formData.pickupEnabled) {
        setSaveMessage({ type: 'error', text: 'Debes habilitar al menos una opción: envío a domicilio o retiro por sucursal.' })
        return
      }
      if (formData.pickupEnabled && (!Array.isArray(formData.pickupWarehouseIds) || formData.pickupWarehouseIds.length === 0)) {
        setSaveMessage({ type: 'error', text: 'Para habilitar retiro por sucursal, selecciona al menos una sucursal.' })
        return
      }

      const tiers = (Array.isArray(formData.distanceTiers) ? formData.distanceTiers : [])
        .map((t) => ({
          maxDistance: parseFloat(t.maxDistance),
          cost: parseArsNumber(t.cost)
        }))
        .filter((t) => Number.isFinite(t.maxDistance) && t.maxDistance > 0 && Number.isFinite(t.cost) && t.cost >= 0)
        .sort((a, b) => a.maxDistance - b.maxDistance)

      const originAddress = {
        street: formData.originAddress?.street?.trim() || '',
        apartment: formData.originAddress?.apartment?.trim() || '',
        city: formData.originAddress?.city?.trim() || '',
        postalCode: formData.originAddress?.postalCode?.trim() || '',
        province: formData.originAddress?.province?.trim() || '',
        country: formData.originAddress?.country?.trim() || 'AR'
      }
      const rawCoords = normalizeCoordinates(formData.originCoordinates)
      const hasAddress = !!buildAddressQuery(originAddress)
      let originCoordinates = rawCoords
      if (!originCoordinates && hasAddress) {
        const geocoded = await geocodeAddress(buildAddressQuery(originAddress))
        originCoordinates = geocoded ? { lat: geocoded.lat, lng: geocoded.lng } : null
      }

      await saveShippingSettings({
        freeShippingMinAmount: formData.freeShippingMinAmount,
        freeShippingMaxDistance: formData.freeShippingMaxDistance,
        defaultShippingCost: formData.defaultShippingCost,
        enabled: formData.homeDeliveryEnabled || formData.pickupEnabled || formData.negotiationEnabled,
        fulfillment: {
          homeDeliveryEnabled: formData.homeDeliveryEnabled,
          pickupEnabled: formData.pickupEnabled,
          pickupWarehouseIds: Array.isArray(formData.pickupWarehouseIds) ? formData.pickupWarehouseIds : []
        },
        distancePricing: {
          originAddress,
          originCoordinates,
          tiers,
          negotiationDistance: formData.negotiationDistance ? parseFloat(formData.negotiationDistance) : null,
          negotiationEnabled: formData.negotiationEnabled === true
        }
      })
      setSaveMessage({ type: 'success', text: 'Configuraciones guardadas correctamente' })
      await refetch() // Recargar configuraciones
      
      // Limpiar mensaje después de 3 segundos
      setTimeout(() => setSaveMessage(null), 3000)
    } catch (error) {
      console.error('Error guardando configuraciones:', error)
      setSaveMessage({ type: 'error', text: `Error al guardar: ${error.message || 'Error desconocido'}` })
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <div className="admin-shipping">
        <div className="loading-state">
          <p>Cargando configuraciones...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="admin-shipping">
      <div className="shipping-header">
        <h2>Configuración de Envío</h2>
        <p>Configura las reglas de envío gratis y costos de envío</p>
      </div>

      {error && (
        <div className="error-message">
          <p>Error al cargar configuraciones: {error}</p>
          <Button onClick={refetch} variant="outline" size="small">
            Reintentar
          </Button>
        </div>
      )}

      {saveMessage && (
        <div className={`save-message ${saveMessage.type}`}>
          {saveMessage.text}
        </div>
      )}

      <form onSubmit={handleSubmit} className="shipping-form">
        <div className="form-section">
          <h3 className="section-title">Modalidad de Entrega</h3>
          <div className="form-group checkbox-group">
            <input
              type="checkbox"
              id="homeDeliveryEnabled"
              name="homeDeliveryEnabled"
              checked={formData.homeDeliveryEnabled}
              onChange={handleChange}
              disabled={saving}
            />
            <label htmlFor="homeDeliveryEnabled">Ofrecer envío a domicilio</label>
          </div>
          <div className="form-group checkbox-group">
            <input
              type="checkbox"
              id="pickupEnabled"
              name="pickupEnabled"
              checked={formData.pickupEnabled}
              onChange={handleChange}
              disabled={saving}
            />
            <label htmlFor="pickupEnabled">Permitir retirar por sucursal</label>
          </div>

          {formData.pickupEnabled && (
            <div className="form-group">
              <label>Sucursales disponibles para retiro</label>
              {warehousesLoading && <small className="form-hint">Cargando sucursales...</small>}
              {warehousesError && <small className="form-hint">Error cargando sucursales: {warehousesError}</small>}
              {!warehousesLoading && !warehousesError && warehouses.length === 0 && (
                <small className="form-hint">No hay sucursales creadas. Crea una en “Depósitos” para habilitar retiro.</small>
              )}
              {!warehousesLoading && warehouses.length > 0 && (
                <div style={{ display: 'grid', gap: '0.75rem' }}>
                  {warehouses.map((w) => (
                    <div key={w.id} className="checkbox-group">
                      <input
                        type="checkbox"
                        id={`pickup_${w.id}`}
                        checked={Array.isArray(formData.pickupWarehouseIds) && formData.pickupWarehouseIds.includes(w.id)}
                        onChange={() => togglePickupWarehouse(w.id)}
                        disabled={saving}
                      />
                      <label htmlFor={`pickup_${w.id}`}>
                        {w.name || 'Sucursal'}{w.active === false ? ' (inactiva)' : ''}
                      </label>
                    </div>
                  ))}
                </div>
              )}
              <small className="form-hint">
                Si habilitás retiro, el cliente elegirá una sucursal en el checkout.
              </small>
            </div>
          )}
        </div>

        {/* Costo de envío por defecto */}
        <div className="form-section">
          <h3 className="section-title">Costo de Envío</h3>
          <div className="form-group">
            <label htmlFor="defaultShippingCost">
              Costo de Envío por Defecto <span className="required">*</span>
            </label>
            <input
              type="number"
              id="defaultShippingCost"
              name="defaultShippingCost"
              value={formData.defaultShippingCost}
              onChange={handleChange}
              placeholder="15000"
              min="0"
              step="0.01"
              required
              disabled={saving || !formData.homeDeliveryEnabled}
            />
            <small className="form-hint">
              Costo de envío estándar cuando no aplica envío gratis (en pesos)
            </small>
          </div>
        </div>

        <div className="form-section">
          <h3 className="section-title">Origen y Tarifas por Distancia</h3>
          <div className="form-group">
            <label>Dirección de Origen</label>
            <div className="origin-grid">
              <input
                type="text"
                name="originAddress.street"
                value={formData.originAddress.street}
                onChange={handleChange}
                placeholder="Calle y número"
                disabled={saving || !formData.homeDeliveryEnabled}
              />
              <input
                type="text"
                name="originAddress.apartment"
                value={formData.originAddress.apartment}
                onChange={handleChange}
                placeholder="Piso / Depto"
                disabled={saving || !formData.homeDeliveryEnabled}
              />
              <input
                type="text"
                name="originAddress.city"
                value={formData.originAddress.city}
                onChange={handleChange}
                placeholder="Ciudad"
                disabled={saving || !formData.homeDeliveryEnabled}
              />
              <input
                type="text"
                name="originAddress.province"
                value={formData.originAddress.province}
                onChange={handleChange}
                placeholder="Provincia"
                disabled={saving || !formData.homeDeliveryEnabled}
              />
              <input
                type="text"
                name="originAddress.postalCode"
                value={formData.originAddress.postalCode}
                onChange={handleChange}
                placeholder="Código postal"
                disabled={saving || !formData.homeDeliveryEnabled}
              />
              <input
                type="text"
                name="originAddress.country"
                value={formData.originAddress.country}
                onChange={handleChange}
                placeholder="País"
                disabled={saving || !formData.homeDeliveryEnabled}
              />
            </div>
          </div>
          <div className="form-group">
            <label>Coordenadas de Origen</label>
            <div className="origin-grid">
              <input
                type="number"
                name="originCoordinates.lat"
                value={formData.originCoordinates.lat}
                onChange={handleChange}
                placeholder="Latitud"
                step="0.000001"
                disabled={saving || !formData.homeDeliveryEnabled}
              />
              <input
                type="number"
                name="originCoordinates.lng"
                value={formData.originCoordinates.lng}
                onChange={handleChange}
                placeholder="Longitud"
                step="0.000001"
                disabled={saving || !formData.homeDeliveryEnabled}
              />
            </div>
            <div className="origin-actions">
              <Button
                type="button"
                variant="outline"
                size="small"
                onClick={handleGeocodeOrigin}
                disabled={saving || !formData.homeDeliveryEnabled || originGeoStatus?.type === 'loading'}
              >
                Geolocalizar dirección de origen
              </Button>
              {originGeoStatus && (
                <span className={`origin-status ${originGeoStatus.type}`}>
                  {originGeoStatus.text}
                </span>
              )}
            </div>
            <small className="form-hint">
              Si se dejan vacías, se intentará geolocalizar la dirección al guardar.
            </small>
            <div className="origin-map">
              <MapContainer
                center={originMapPosition || { lat: -34.603722, lng: -58.381592 }}
                zoom={originMapPosition ? 15 : 5}
                scrollWheelZoom={false}
                className="origin-map__canvas"
              >
                <TileLayer
                  attribution='&copy; OpenStreetMap'
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                <MapRecenter center={originMapPosition} />
                <MapClickHandler onChange={handleMapPositionChange} disabled={saving || !formData.homeDeliveryEnabled} />
                {originMapPosition && (
                  <DraggableMarker
                    position={originMapPosition}
                    onChange={handleMapPositionChange}
                    disabled={saving || !formData.homeDeliveryEnabled}
                    icon={markerIconConfig}
                  />
                )}
              </MapContainer>
            </div>
          </div>
          <div className="form-group">
            <label>Tarifas por Distancia</label>
            <div className="distance-tiers">
              {formData.distanceTiers.map((tier, index) => (
                <div key={`tier_${index}`} className="distance-tier-row">
                  <input
                    type="number"
                    value={tier.maxDistance}
                    onChange={(e) => updateTier(index, 'maxDistance', e.target.value)}
                    placeholder="Km hasta"
                    min="0"
                    step="0.1"
                    disabled={saving || !formData.homeDeliveryEnabled}
                  />
                  <input
                    type="text"
                    value={tier.cost}
                    onChange={(e) => handleTierCostChange(index, e.target.value)}
                    onBlur={() => handleTierCostBlur(index)}
                    onFocus={() => handleTierCostFocus(index)}
                    placeholder="Costo"
                    disabled={saving || !formData.homeDeliveryEnabled}
                  />
                  <button
                    type="button"
                    className="tier-remove"
                    onClick={() => removeTier(index)}
                    disabled={saving || !formData.homeDeliveryEnabled}
                  >
                    Quitar
                  </button>
                </div>
              ))}
            </div>
            <button
              type="button"
              className="tier-add"
              onClick={addTier}
              disabled={saving || !formData.homeDeliveryEnabled}
            >
              Agregar tarifa
            </button>
          </div>
          <div className="form-group checkbox-group">
            <input
              type="checkbox"
              id="negotiationEnabled"
              name="negotiationEnabled"
              checked={formData.negotiationEnabled}
              onChange={handleChange}
              disabled={saving || !formData.homeDeliveryEnabled}
            />
            <label htmlFor="negotiationEnabled">Permitir acordar envío con el vendedor</label>
          </div>
          <div className="form-group">
            <label htmlFor="negotiationDistance">
              Acordar envío cuando supere (km)
            </label>
            <input
              type="number"
              id="negotiationDistance"
              name="negotiationDistance"
              value={formData.negotiationDistance || ''}
              onChange={handleChange}
              placeholder="Ej: 30"
              min="0"
              step="0.1"
              disabled={saving || !formData.homeDeliveryEnabled}
            />
            <small className="form-hint">
              Si la distancia supera este valor, el cliente solo podrá acordar el envío.
            </small>
          </div>
        </div>

        {/* Reglas de envío gratis */}
        <div className="form-section">
          <h3 className="section-title">Envío Gratis</h3>
          
          <div className="form-group">
            <label htmlFor="freeShippingMinAmount">
              Envío Gratis a partir de (Monto Mínimo) <span className="required">*</span>
            </label>
            <input
              type="number"
              id="freeShippingMinAmount"
              name="freeShippingMinAmount"
              value={formData.freeShippingMinAmount}
              onChange={handleChange}
              placeholder="50000"
              min="0"
              step="0.01"
              required
              disabled={saving || !formData.homeDeliveryEnabled}
            />
            <small className="form-hint">
              Monto mínimo del pedido para que el envío sea gratis (en pesos). 
              Si el subtotal es igual o mayor a este monto, el envío será gratis.
            </small>
          </div>

          <div className="form-group">
            <label htmlFor="freeShippingMaxDistance">
              Envío Gratis hasta (Distancia Máxima en km) <small>(opcional)</small>
            </label>
            <input
              type="number"
              id="freeShippingMaxDistance"
              name="freeShippingMaxDistance"
              value={formData.freeShippingMaxDistance || ''}
              onChange={handleChange}
              placeholder="Sin límite"
              min="0"
              step="0.1"
              disabled={saving || !formData.homeDeliveryEnabled}
            />
            <small className="form-hint">
              Distancia máxima en kilómetros para que aplique el envío gratis. 
              Si se deja vacío, no hay límite de distancia. 
              Si la distancia es mayor a este valor, se cobrará el envío aunque el monto sea suficiente.
            </small>
          </div>
        </div>

        {/* Información adicional */}
        <div className="info-box">
          <h4>📋 Cómo funciona:</h4>
          <ul>
            <li>
              Podés habilitar <strong>envío a domicilio</strong>, <strong>retiro por sucursal</strong> o <strong>ambos</strong>.
              En el checkout, el cliente elegirá según lo que habilites.
            </li>
            <li>
              Si el <strong>subtotal del pedido</strong> es igual o mayor al <strong>Monto Mínimo</strong>, 
              el envío será gratis.
            </li>
            <li>
              Si se configura una <strong>Distancia Máxima</strong>, el envío gratis solo aplicará 
              si la distancia del envío es menor o igual a ese valor.
            </li>
            <li>
              Si no se cumple ninguna condición de envío gratis, se cobrará el <strong>Costo de Envío por Defecto</strong>.
            </li>
          </ul>
        </div>

        {/* Botones de acción */}
        <div className="form-actions">
          <Button
            type="submit"
            variant="primary"
            disabled={saving}
          >
            {saving ? 'Guardando...' : 'Guardar Configuraciones'}
          </Button>
        </div>
      </form>
    </div>
  )
}

export default AdminShipping

